import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    TextInput,
    ActivityIndicator,
    Alert,
    Platform,
    RefreshControl,
    Modal
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AdminLayout from '../components/layout/AdminLayout';
import { getColor, getSpacing, getBorderRadius, getShadow } from '../theme/adminTheme';
import api, { getData, putData, postData } from '../../services/api';

const ACTIONS = [
    { key: 'POST_CREATE', label: 'Create Post', default: 1 },
    { key: 'POST_LIKE', label: 'Like Post', default: 0.5 },
    { key: 'POST_COMMENT', label: 'Comment on Post', default: 0.5 },
    { key: 'PROFILE_COMPLETE', label: 'Complete Profile', default: 10 },
    { key: 'MARKETPLACE_LIST', label: 'List Item on Marketplace', default: 15 },
    { key: 'LOAN_REPAY', label: 'Repay Loan', default: 5 },
    { key: 'DAILY_LOGIN', label: 'Daily Login', default: 5 },
    { key: 'EVENT_JOIN', label: 'Join Event', default: 15 },
    { key: 'SPACE_JOIN', label: 'Join Mentor Space', default: 5 },
    { key: 'INSPIRATION_WATCH', label: 'Watch Daily Inspiration', default: 1 },
];

const REQUIREMENT_TYPES = [
    { label: 'Posts Created', value: 'POST_COUNT' },
    { label: 'Total XP Earned', value: 'POINTS_TOTAL' },
    { label: 'Marketplace Deals', value: 'MARKETPLACE_LIST_COUNT' },
    { label: 'Likes Received', value: 'POST_LIKE_COUNT' },
    { label: 'Comments Made', value: 'POST_COMMENT_COUNT' },
    { label: 'Profile Completion', value: 'PROFILE_COMPLETE' },
    { label: 'Spaces Joined', value: 'SPACE_JOIN_COUNT' },
    { label: 'Inspirations Watched', value: 'INSPIRATION_WATCH_COUNT' },
];

const GamificationManagementScreen = () => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    
    // Global disburse state
    const [disburseAmount, setDisburseAmount] = useState('');
    const [disburseReason, setDisburseReason] = useState('');
    const [disbursing, setDisbursing] = useState(false);

    // Awards state
    const [awards, setAwards] = useState([]);
    const [isAwardModalVisible, setIsAwardModalVisible] = useState(false);
    const [currentAward, setCurrentAward] = useState(null);
    const [awardForm, setAwardForm] = useState({
        title: '',
        description: '',
        icon: 'star',
        color: '#6366F1',
        isAutomatic: false,
        requirementType: '',
        requirementValue: ''
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await getData('/admin/gamification/settings');
            if (response.success) {
                const settingsMap = {};
                response.data.forEach(s => {
                    settingsMap[s.key] = s.value;
                });
                setSettings(settingsMap);
            }
            
            // Also fetch awards
            const awardsResponse = await getData('/admin/awards');
            if (awardsResponse.success) {
                setAwards(awardsResponse.data);
            }
        } catch (error) {
            console.error('Error fetching gamification data:', error);
            Alert.alert('Error', 'Failed to fetch settings');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleSaveSettings = async () => {
        try {
            setSaving(true);
            const settingsArray = Object.entries(settings).map(([key, value]) => ({
                key,
                value
            }));
            
            // Also include defaults that aren't in the settings map yet
            ACTIONS.forEach(action => {
                const key = `GAMIF_${action.key}`;
                if (!settings[key]) {
                    settingsArray.push({ key, value: String(action.default) });
                }
            });

            const response = await putData('/admin/gamification/settings', { settings: settingsArray });
            if (response.success) {
                Alert.alert('Success', 'Settings updated successfully');
                fetchSettings();
            }
        } catch (error) {
            console.error('Error saving gamification settings:', error);
            Alert.alert('Error', 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleDisburseGlobal = async () => {
        if (!disburseAmount || isNaN(disburseAmount)) {
            Alert.alert('Error', 'Please enter a valid amount');
            return;
        }

        if (!confirm(`Are you sure you want to disburse ${disburseAmount} points to ALL active users?`)) return;

        try {
            setDisbursing(true);
            const response = await postData('/admin/gamification/disburse', {
                points: parseFloat(disburseAmount),
                reason: disburseReason
            });

            if (response.success) {
                Alert.alert('Success', response.message);
                setDisburseAmount('');
                setDisburseReason('');
            }
        } catch (error) {
            console.error('Error disbursing global awards:', error);
            Alert.alert('Error', 'Failed to disburse points');
        } finally {
            setDisbursing(false);
        }
    };

    const updateValue = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleEditAward = (award) => {
        setCurrentAward(award);
        setAwardForm({
            title: award.title,
            description: award.description || '',
            icon: award.icon || 'star',
            color: award.color || '#6366F1',
            isAutomatic: !!award.isAutomatic,
            requirementType: award.requirementType || '',
            requirementValue: award.requirementValue ? String(award.requirementValue) : ''
        });
        setIsAwardModalVisible(true);
    };

    const handleAddAward = () => {
        setCurrentAward(null);
        setAwardForm({
            title: '',
            description: '',
            icon: 'star',
            color: '#6366F1',
            isAutomatic: false,
            requirementType: '',
            requirementValue: ''
        });
        setIsAwardModalVisible(true);
    };

    const handleSaveAward = async () => {
        if (!awardForm.title) {
            Alert.alert('Error', 'Award title is required');
            return;
        }

        try {
            setSaving(true);
            let response;
            if (currentAward) {
                response = await putData(`/admin/awards/${currentAward.id}`, awardForm);
            } else {
                response = await postData('/admin/awards', awardForm);
            }

            if (response.success) {
                setIsAwardModalVisible(false);
                fetchSettings();
            }
        } catch (error) {
            Alert.alert('Error', 'Failed to save award');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAward = (id) => {
        Alert.alert(
            'Delete Award',
            'Are you sure you want to delete this award?',
            [
                { text: 'Cancel', style: 'cancel' },
                { 
                    text: 'Delete', 
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            const response = await api.delete(`/admin/awards/${id}`);
                            if (response.data.success) {
                                fetchSettings();
                            }
                        } catch (error) {
                            Alert.alert('Error', 'Failed to delete award');
                        }
                    }
                }
            ]
        );
    };

    if (loading && !refreshing) {
        return (
            <AdminLayout>
                <View style={styles.centered}>
                    <ActivityIndicator size="large" color={getColor('primary.600')} />
                </View>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <ScrollView 
                style={styles.container} 
                contentContainerStyle={styles.content}
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => {setRefreshing(true); fetchSettings();}} />}
            >
                <View style={styles.header}>
                    <View>
                        <Text style={styles.title}>Gamification Management</Text>
                        <Text style={styles.subtitle}>Configure global reward rules and disburse collective awards</Text>
                    </View>
                    <TouchableOpacity 
                        style={[styles.saveButton, saving && styles.disabledButton]} 
                        onPress={handleSaveSettings}
                        disabled={saving}
                    >
                        {saving ? <ActivityIndicator size="small" color="#FFF" /> : <Text style={styles.saveButtonText}>Save All Rules</Text>}
                    </TouchableOpacity>
                </View>

                <View style={styles.grid}>
                    {/* Rules Section */}
                    <View style={[styles.card, styles.rulesCard, getShadow('md')]}>
                        <View style={styles.cardHeader}>
                            <Ionicons name="flash-outline" size={20} color={getColor('primary.600')} />
                            <Text style={styles.cardTitle}>Reward Rules (XP per Action)</Text>
                        </View>
                        <View style={styles.rulesList}>
                            {ACTIONS.map((action) => {
                                const key = `GAMIF_${action.key}`;
                                const val = settings[key] !== undefined ? settings[key] : String(action.default);
                                return (
                                    <View key={action.key} style={styles.ruleItem}>
                                        <View style={styles.ruleInfo}>
                                            <Text style={styles.ruleLabel}>{action.label}</Text>
                                            <Text style={styles.ruleKey}>{action.key}</Text>
                                        </View>
                                        <TextInput
                                            style={styles.ruleInput}
                                            value={String(val)}
                                            onChangeText={(v) => updateValue(action.key, v)}
                                            keyboardType="decimal-pad"
                                            placeholder={String(action.default)}
                                        />
                                    </View>
                                );
                            })}
                        </View>
                    </View>

                    {/* Global Disbursement Section */}
                    <View style={[styles.card, styles.disburseCard, getShadow('md')]}>
                        <View style={styles.cardHeader}>
                            <Ionicons name="trophy-outline" size={20} color={getColor('warning.600')} />
                            <Text style={styles.cardTitle}>Global Collective Award</Text>
                        </View>
                        <View style={styles.disburseBody}>
                            <Text style={styles.disburseDesc}>
                                Give points to every active user in the system at once.
                            </Text>
                            
                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Points Amount</Text>
                                <View style={styles.pointsInputBox}>
                                    <TextInput
                                        style={styles.disburseInput}
                                        value={disburseAmount}
                                        onChangeText={setDisburseAmount}
                                        placeholder="e.g. 50"
                                        keyboardType="number-pad"
                                    />
                                    <View style={styles.pointsIndicator}>
                                        <Text style={styles.pointsIndicatorText}>XP</Text>
                                    </View>
                                </View>
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Reason / Note (Optional)</Text>
                                <TextInput
                                    style={[styles.disburseInput, styles.textArea]}
                                    value={disburseReason}
                                    onChangeText={setDisburseReason}
                                    placeholder="e.g. Monthly Community Bonus"
                                    multiline
                                    numberOfLines={3}
                                />
                            </View>

                            <TouchableOpacity 
                                style={[styles.disburseButton, disbursing && styles.disabledButton]} 
                                onPress={handleDisburseGlobal}
                                disabled={disbursing}
                            >
                                {disbursing ? (
                                    <ActivityIndicator size="small" color="#FFF" />
                                ) : (
                                    <>
                                        <Ionicons name="gift-outline" size={18} color="#FFF" style={{marginRight: 8}} />
                                        <Text style={styles.disburseButtonText}>Disburse to All Users</Text>
                                    </>
                                )}
                            </TouchableOpacity>
                        </View>

                        {/* XP Redemption Settings Section */}
                        <View style={[styles.card, { marginTop: getSpacing('lg'), padding: 0, borderTopWidth: 1, borderTopColor: getColor('gray.100') }]}>
                            <View style={[styles.cardHeader, { marginTop: getSpacing('lg'), borderBottomWidth: 0 }]}>
                                <Ionicons name="wallet-outline" size={20} color={getColor('success.500')} />
                                <Text style={styles.cardTitle}>XP Redemption Settings</Text>
                            </View>
                            <View style={styles.disburseBody}>
                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Redemption Rate (XP per 1 unit)</Text>
                                    <TextInput
                                        style={styles.ruleInput}
                                        value={settings['XP_REDEMPTION_RATE'] || '100'}
                                        onChangeText={(v) => updateValue('XP_REDEMPTION_RATE', v)}
                                        keyboardType="decimal-pad"
                                    />
                                    <Text style={styles.ruleKey}>e.g. 100 means 100 XP = 1 Money</Text>
                                </View>
                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Minimum XP to Redeem</Text>
                                    <TextInput
                                        style={styles.ruleInput}
                                        value={settings['XP_REDEMPTION_MIN'] || '500'}
                                        onChangeText={(v) => updateValue('XP_REDEMPTION_MIN', v)}
                                        keyboardType="decimal-pad"
                                    />
                                </View>
                            </View>
                        </View>
                    </View>
                </View>

                {/* Awards Management Section */}
                <View style={[styles.card, { marginTop: getSpacing('lg') }, getShadow('md')]}>
                    <View style={[styles.cardHeader, { justifyContent: 'space-between', borderBottomWidth: 0 }]}>
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Ionicons name="medal-outline" size={22} color={getColor('primary.600')} />
                            <Text style={[styles.cardTitle, { fontSize: 20 }]}>Badges & Awards</Text>
                        </View>
                        <TouchableOpacity style={styles.addAwardButton} onPress={handleAddAward}>
                            <Ionicons name="add" size={20} color="#FFF" />
                            <Text style={styles.addAwardText}>New Award</Text>
                        </TouchableOpacity>
                    </View>
                    
                    <View style={styles.awardsList}>
                        {awards.length === 0 ? (
                            <View style={styles.emptyContainer}>
                                <Text style={styles.emptyText}>No awards created yet</Text>
                            </View>
                        ) : (
                            awards.map((award) => (
                                <View key={award.id} style={styles.awardItem}>
                                    <View style={[styles.awardIconCircle, { backgroundColor: award.color + '22' }]}>
                                        <Ionicons name={award.icon || 'star'} size={24} color={award.color} />
                                    </View>
                                    <View style={styles.awardDetails}>
                                        <Text style={styles.awardTitleText}>{award.title}</Text>
                                        <Text style={styles.awardDescText} numberOfLines={1}>{award.description || 'No description'}</Text>
                                        {award.requirementType && (
                                            <Text style={styles.awardReqText}>Require: {award.requirementValue} {award.requirementType}</Text>
                                        )}
                                    </View>
                                    <View style={styles.awardActions}>
                                        <TouchableOpacity onPress={() => handleEditAward(award)} style={styles.iconBtn}>
                                            <Ionicons name="create-outline" size={18} color={getColor('gray.600')} />
                                        </TouchableOpacity>
                                        <TouchableOpacity onPress={() => handleDeleteAward(award.id)} style={styles.iconBtn}>
                                            <Ionicons name="trash-outline" size={18} color={getColor('danger.500')} />
                                        </TouchableOpacity>
                                    </View>
                                </View>
                            ))
                        )}
                    </View>
                </View>

                {/* Award Modal */}
                <Modal visible={isAwardModalVisible} transparent animationType="fade">
                    <View style={styles.modalOverlay}>
                        <View style={styles.modalCard}>
                            <Text style={styles.modalHeaderTitle}>{currentAward ? 'Edit Award' : 'Create New Award'}</Text>
                            
                            <ScrollView style={{ maxHeight: 700 }}>
                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Award Title</Text>
                                    <TextInput
                                        style={styles.modalInput}
                                        value={awardForm.title}
                                        onChangeText={(v) => setAwardForm({ ...awardForm, title: v })}
                                        placeholder="e.g. Social Butterfly"
                                    />
                                </View>

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Description</Text>
                                    <TextInput
                                        style={[styles.modalInput, styles.textArea]}
                                        value={awardForm.description}
                                        onChangeText={(v) => setAwardForm({ ...awardForm, description: v })}
                                        placeholder="Awarded for creating 10 posts"
                                        multiline
                                    />
                                </View>

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Requirement Type</Text>
                                    <View style={styles.reqTypeGrid}>
                                        {REQUIREMENT_TYPES.map((type) => (
                                            <TouchableOpacity 
                                                key={type.value}
                                                style={[
                                                    styles.reqTypeChip,
                                                    awardForm.requirementType === type.value && styles.reqTypeChipActive
                                                ]}
                                                onPress={() => setAwardForm({ ...awardForm, requirementType: type.value })}
                                            >
                                                <Text style={[
                                                    styles.reqTypeChipText,
                                                    awardForm.requirementType === type.value && styles.reqTypeChipTextActive
                                                ]}>
                                                    {type.label}
                                                </Text>
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                </View>

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Threshold Value (Number)</Text>
                                    <TextInput
                                        style={styles.modalInput}
                                        value={awardForm.requirementValue}
                                        onChangeText={(v) => setAwardForm({ ...awardForm, requirementValue: v })}
                                        placeholder="e.g. 10"
                                        keyboardType="number-pad"
                                    />
                                </View>

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Select Icon</Text>
                                    <View style={styles.iconPickerGrid}>
                                        {[
                                            'star', 'medal', 'trophy', 'ribbon', 
                                            'flask', 'book', 'rocket', 'heart', 
                                            'people', 'calendar', 'bulb', 'leaf', 
                                            'shield', 'gift', 'extension-puzzle', 'bicycle',
                                            'briefcase', 'school', 'earth', 'hammer'
                                        ].map(iconName => (
                                            <TouchableOpacity 
                                                key={iconName}
                                                style={[
                                                    styles.iconPickerItem,
                                                    awardForm.icon === iconName && { backgroundColor: awardForm.color + '22', borderColor: awardForm.color }
                                                ]}
                                                onPress={() => setAwardForm({ ...awardForm, icon: iconName })}
                                            >
                                                <Ionicons 
                                                    name={iconName} 
                                                    size={20} 
                                                    color={awardForm.icon === iconName ? awardForm.color : getColor('gray.400')} 
                                                />
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                </View>

                                <View style={styles.inputGroup}>
                                    <Text style={styles.label}>Select Color</Text>
                                    <View style={styles.colorPickerGrid}>
                                        {[
                                            '#6366F1', '#F43F5E', '#10B981', '#F59E0B', 
                                            '#0EA5E9', '#8B5CF6', '#D946EF', '#F97316',
                                            '#06B6D4', '#84CC16', '#71717A', '#111827'
                                        ].map(colorCode => (
                                            <TouchableOpacity 
                                                key={colorCode}
                                                style={[
                                                    styles.colorPickerItem,
                                                    { backgroundColor: colorCode },
                                                    awardForm.color === colorCode && styles.colorPickerItemActive
                                                ]}
                                                onPress={() => setAwardForm({ ...awardForm, color: colorCode })}
                                            >
                                                {awardForm.color === colorCode && (
                                                    <Ionicons name="checkmark" size={16} color="#FFF" />
                                                )}
                                            </TouchableOpacity>
                                        ))}
                                    </View>
                                </View>
                            </ScrollView>

                            <View style={styles.modalFooter}>
                                <TouchableOpacity 
                                    style={styles.cancelBtn} 
                                    onPress={() => setIsAwardModalVisible(false)}
                                >
                                    <Text style={styles.cancelBtnText}>Cancel</Text>
                                </TouchableOpacity>
                                <TouchableOpacity 
                                    style={styles.confirmBtn} 
                                    onPress={handleSaveAward}
                                >
                                    <Text style={styles.confirmBtnText}>Save Award</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>
            </ScrollView>
        </AdminLayout>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: getColor('gray.50'),
    },
    content: {
        padding: getSpacing('lg'),
    },
    centered: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: getSpacing('xl'),
    },
    title: {
        fontSize: 28,
        fontWeight: '700',
        color: getColor('gray.900'),
        marginBottom: 4,
    },
    subtitle: {
        fontSize: 14,
        color: getColor('gray.600'),
    },
    saveButton: {
        backgroundColor: getColor('primary.600'),
        paddingHorizontal: getSpacing('lg'),
        paddingVertical: 12,
        borderRadius: getBorderRadius('md'),
        minWidth: 120,
        alignItems: 'center',
    },
    saveButtonText: {
        color: '#FFF',
        fontWeight: '600',
    },
    grid: {
        flexDirection: Platform.OS === 'web' ? 'row' : 'column',
        gap: getSpacing('lg'),
    },
    card: {
        backgroundColor: '#FFF',
        borderRadius: getBorderRadius('lg'),
        padding: getSpacing('lg'),
        flex: 1,
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: getSpacing('lg'),
        paddingBottom: getSpacing('md'),
        borderBottomWidth: 1,
        borderBottomColor: getColor('gray.100'),
    },
    cardTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: getColor('gray.900'),
        marginLeft: getSpacing('sm'),
    },
    rulesList: {
        gap: getSpacing('md'),
    },
    ruleItem: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: getSpacing('xs'),
    },
    ruleInfo: {
        flex: 1,
    },
    ruleLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: getColor('gray.800'),
    },
    ruleKey: {
        fontSize: 10,
        color: getColor('gray.500'),
        marginTop: 2,
    },
    ruleInput: {
        width: 80,
        backgroundColor: getColor('gray.50'),
        borderWidth: 1,
        borderColor: getColor('gray.200'),
        borderRadius: getBorderRadius('md'),
        padding: 8,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: '700',
        color: getColor('primary.600'),
    },
    disburseBody: {
        gap: getSpacing('lg'),
    },
    disburseDesc: {
        fontSize: 14,
        color: getColor('gray.600'),
        lineHeight: 20,
    },
    inputGroup: {
        gap: getSpacing('xs'),
    },
    label: {
        fontSize: 13,
        fontWeight: '600',
        color: getColor('gray.700'),
    },
    pointsInputBox: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: getColor('gray.50'),
        borderWidth: 1,
        borderColor: getColor('gray.200'),
        borderRadius: getBorderRadius('md'),
    },
    disburseInput: {
        flex: 1,
        padding: 12,
        fontSize: 14,
        color: getColor('gray.900'),
        ...(Platform.OS === 'web' ? { outline: 'none' } : {}),
    },
    pointsIndicator: {
        backgroundColor: getColor('gray.200'),
        paddingHorizontal: 12,
        height: '100%',
        justifyContent: 'center',
        borderTopRightRadius: getBorderRadius('md'),
        borderBottomRightRadius: getBorderRadius('md'),
    },
    pointsIndicatorText: {
        fontSize: 12,
        fontWeight: '700',
        color: getColor('gray.600'),
    },
    textArea: {
        height: 80,
        textAlignVertical: 'top',
        backgroundColor: getColor('gray.50'),
        borderWidth: 1,
        borderColor: getColor('gray.200'),
        borderRadius: getBorderRadius('md'),
    },
    disburseButton: {
        backgroundColor: getColor('warning.600'),
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 14,
        borderRadius: getBorderRadius('md'),
        marginTop: getSpacing('md'),
    },
    disburseButtonText: {
        color: '#FFF',
        fontWeight: '700',
        fontSize: 14,
    },
    disabledButton: {
        opacity: 0.6,
    },
    addAwardButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: getColor('primary.600'),
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        gap: 6
    },
    addAwardText: {
        color: '#FFF',
        fontWeight: '600',
        fontSize: 12
    },
    awardsList: {
        marginTop: getSpacing('md'),
        gap: getSpacing('md')
    },
    awardItem: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: getSpacing('md'),
        backgroundColor: getColor('gray.50'),
        borderRadius: 12,
        borderWidth: 1,
        borderColor: getColor('gray.100')
    },
    awardIconCircle: {
        width: 44,
        height: 44,
        borderRadius: 22,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: getSpacing('md')
    },
    awardDetails: {
        flex: 1
    },
    awardTitleText: {
        fontSize: 15,
        fontWeight: '700',
        color: getColor('gray.900')
    },
    awardDescText: {
        fontSize: 12,
        color: getColor('gray.500'),
        marginTop: 2
    },
    awardReqText: {
        fontSize: 10,
        fontWeight: '600',
        color: getColor('primary.600'),
        marginTop: 2
    },
    reqTypeGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: getSpacing('xs'),
        marginTop: 4
    },
    reqTypeChip: {
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
        backgroundColor: getColor('gray.100'),
        borderWidth: 1,
        borderColor: getColor('gray.200')
    },
    reqTypeChipActive: {
        backgroundColor: getColor('primary.50'),
        borderColor: getColor('primary.500')
    },
    reqTypeChipText: {
        fontSize: 12,
        color: getColor('gray.600'),
        fontWeight: '500'
    },
    reqTypeChipTextActive: {
        color: getColor('primary.700'),
        fontWeight: '700'
    },
    awardActions: {
        flexDirection: 'row',
        gap: getSpacing('sm')
    },
    iconBtn: {
        padding: 8,
        borderRadius: 8,
        backgroundColor: '#FFF',
        borderWidth: 1,
        borderColor: getColor('gray.200')
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20
    },
    modalCard: {
        backgroundColor: '#FFF',
        width: '100%',
        maxWidth: 600,
        borderRadius: 16,
        padding: 24,
        gap: 20
    },
    modalHeaderTitle: {
        fontSize: 20,
        fontWeight: '700',
        color: getColor('gray.900')
    },
    modalInput: {
        backgroundColor: getColor('gray.50'),
        borderWidth: 1,
        borderColor: getColor('gray.200'),
        borderRadius: 8,
        padding: 12,
        fontSize: 14
    },
    modalFooter: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
        marginTop: 10
    },
    cancelBtn: {
        paddingVertical: 10,
        paddingHorizontal: 20
    },
    cancelBtnText: {
        color: getColor('gray.600'),
        fontWeight: '600'
    },
    confirmBtn: {
        backgroundColor: getColor('primary.600'),
        paddingVertical: 10,
        paddingHorizontal: 20,
        borderRadius: 8
    },
    confirmBtnText: {
        color: '#FFF',
        fontWeight: '600'
    },
    emptyContainer: {
        padding: 40,
        alignItems: 'center'
    },
    emptyText: {
        color: getColor('gray.400'),
        fontStyle: 'italic'
    },
    iconPickerGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        marginTop: 8
    },
    iconPickerItem: {
        width: 48,
        height: 48,
        borderRadius: 10,
        backgroundColor: getColor('gray.50'),
        borderWidth: 2,
        borderColor: 'transparent',
        alignItems: 'center',
        justifyContent: 'center'
    },
    colorPickerGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 8
    },
    colorPickerItem: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 2,
        borderColor: 'transparent'
    },
    colorPickerItemActive: {
        borderColor: '#000',
        transform: [{ scale: 1.1 }]
    }
});

export default GamificationManagementScreen;
